'use client';

import { useState } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Badge } from '../../../components/badge';
import { DetailRow } from '../../../components/detail-row/DetailRow';
import { IconButton } from '../../../components/icon-button';
import { Menu } from '../../../components/menu';
import { MetaDefault, BodyDefault } from '../../../components/typography';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  MoreVerticalIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { formatAdCredentialIdentity, automationId } from '@terra/shared-utils';
import type { InternalNetworkCredentialDto } from '@terra/shared-contracts-internal-network';
import { InternalNetworkCredentialVerificationBadge } from './InternalNetworkCredentialVerificationBadge';
import { InternalNetworkCredentialOwnedBadge } from './InternalNetworkCredentialOwnedBadge';
import { InternalNetworkCredentialVerificationInfo } from './InternalNetworkCredentialVerificationInfo';
import {
  resolveInternalNetworkCredentialAuthMethodLabelKey,
  type InternalNetworkCredentialVerificationStatus,
} from './internal-credentials.model';

export type InternalNetworkCredentialOwnedToggle = {
  credentialId: string;
  isOwned: boolean;
};

type InternalNetworkCredentialCardProps = {
  credential: InternalNetworkCredentialDto;
  verificationStatus: InternalNetworkCredentialVerificationStatus;
  verificationError?: string;
  isTesting?: boolean;
  isTestDisabled?: boolean;
  isOwnedTogglePending?: boolean;
  isReadOnly?: boolean;
  onEdit: (credential: InternalNetworkCredentialDto) => void;
  onDelete: (credential: InternalNetworkCredentialDto) => void;
  onTest: (credentialId: string) => void;
  onToggleOwned: (toggle: InternalNetworkCredentialOwnedToggle) => void;
};

export const InternalNetworkCredentialCard = ({
  credential,
  verificationStatus,
  verificationError,
  isTesting,
  isTestDisabled,
  isOwnedTogglePending,
  isReadOnly,
  onEdit,
  onDelete,
  onTest,
  onToggleOwned,
}: InternalNetworkCredentialCardProps) => {
  const tCard = useTranslations('designSystem.credentials.card');
  const t = useTranslations('designSystem.networkSettings.internalCredentials');
  const [isExpanded, setIsExpanded] = useState(false);
  const identity = formatAdCredentialIdentity({
    username: credential.username,
    domain: credential.domain,
  });
  const isVerified =
    verificationStatus === 'VERIFIED' || credential.lastVerifiedAt !== null;
  const canMarkOwned =
    !isReadOnly && !credential.isOwned && !credential.isDisabled;
  const canRemoveOwned = credential.isOwned && !isReadOnly;
  const authMethodLabelKey =
    resolveInternalNetworkCredentialAuthMethodLabelKey(credential);
  const menuItems = [
    {
      value: 'edit',
      label: tCard('menu.edit'),
      disabled: isReadOnly,
      onClick: () => {
        if (!isReadOnly) {
          onEdit(credential);
        }
      },
    },
    {
      value: 'test',
      label: tCard('menu.test'),
      disabled: isTesting || isTestDisabled,
      onClick: () => {
        onTest(credential.id);
      },
    },
    ...(canMarkOwned
      ? [
          {
            value: 'markOwned',
            label: t('markOwned'),
            disabled: isOwnedTogglePending || !isVerified,
            onClick: () => {
              if (!isVerified || isOwnedTogglePending) {
                return;
              }

              onToggleOwned({ credentialId: credential.id, isOwned: true });
            },
          },
        ]
      : []),
    ...(canRemoveOwned
      ? [
          {
            value: 'removeOwned',
            label: t('removeOwned'),
            disabled: isOwnedTogglePending,
            onClick: () => {
              onToggleOwned({ credentialId: credential.id, isOwned: false });
            },
          },
        ]
      : []),
    {
      value: 'delete',
      label: tCard('menu.delete'),
      disabled: isReadOnly,
      dataTestid: 'internal-network-credential-menu-delete',
      onClick: () => {
        if (!isReadOnly) {
          onDelete(credential);
        }
      },
    },
  ];

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.subtle"
      borderRadius="md"
      bg="bg.neutral.default"
      shadow="xs"
      overflow="hidden"
    >
      <HStack
        gap={2}
        px={4}
        py={3}
        alignItems="center"
        cursor="pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <IconButton
          variant="ghost"
          size="xs"
          aria-label={
            isExpanded ? tCard('aria.collapse') : tCard('aria.expand')
          }
          onClick={(event) => {
            event.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? (
            <ArrowDown01Icon size={16} />
          ) : (
            <ArrowRight01Icon size={16} />
          )}
        </IconButton>

        <VStack alignItems="flex-start" gap={1.5} flex={1} minW={0}>
          <HStack gap={1} alignItems="center" w="full" flexWrap="wrap">
            <BodyDefault
              fontWeight="medium"
              color="fg.neutral.default"
              truncate
              {...automationId('internal-network-credential-name')}
            >
              {credential.name || credential.username || tCard('noUsername')}
            </BodyDefault>
            <Badge variant="outline" size="sm">
              {t(`credentialTypes.${credential.credentialType}`)}
            </Badge>
            <InternalNetworkCredentialVerificationBadge
              status={verificationStatus}
            />
            {credential.isOwned ? (
              <InternalNetworkCredentialOwnedBadge />
            ) : null}
            {credential.isHvt ? (
              <Badge variant="fill" size="sm" colorPalette="orange">
                {t('crownJewel')}
              </Badge>
            ) : null}
          </HStack>
          <MetaDefault color="fg.neutral.muted" truncate w="full">
            {identity}
          </MetaDefault>
          <InternalNetworkCredentialVerificationInfo
            verificationStatus={verificationStatus}
            verifiedAt={credential.lastVerifiedAt}
            isTesting={isTesting}
          />
          {verificationStatus === 'FAILED' && verificationError ? (
            <MetaDefault color="fg.neutral.error">
              {verificationError}
            </MetaDefault>
          ) : null}
        </VStack>

        <HStack gap={2} onClick={(event) => event.stopPropagation()}>
          <Menu.MenuWithItems
            items={menuItems}
            trigger={
              <IconButton
                variant="outline"
                size="xs"
                aria-label={tCard('menu.label')}
                {...automationId('internal-network-credential-actions')}
              >
                <MoreVerticalIcon size={16} />
              </IconButton>
            }
          />
        </HStack>
      </HStack>

      {isExpanded ? (
        <Box px={4} pb={4}>
          <VStack alignItems="stretch" gap={0} w="full">
            <DetailRow label={t('username')} value={credential.username} />
            <DetailRow label={t('domain')} value={credential.domain} />
            <DetailRow
              label={t('credentialType')}
              value={t(`credentialTypes.${credential.credentialType}`)}
            />
            <DetailRow
              label={t('authMethod')}
              value={authMethodLabelKey ? t(authMethodLabelKey) : '-'}
            />
          </VStack>
        </Box>
      ) : null}
    </Box>
  );
};
